/**
 * <pg-product-gallery> (markup: snippets/pg-product-gallery.liquid).
 *
 * A native scroll-snap strip: swipe, trackpad and keyboard scrolling work without JS. This
 * adds thumbnails / dots / arrows that follow the scroll position, jumps to the variant image
 * on `pg:variant:change`, pauses videos that scroll away, loads YouTube/Vimeo/3D on request,
 * and opens the zoom dialog (assets/pg-product-zoom.js, imported on first click).
 */
import { PgElement, define, reducedMotion } from '@pg/core';

class PgProductGallery extends PgElement {
  #index = 0;
  #frame = 0;

  mount() {
    const { track, prev, next } = this.refs;
    if (!track) return;
    this.slides = /** @type {HTMLElement[]} */ (this.refs.slides || []);

    track.addEventListener('scroll', () => this.#schedule(), { passive: true, signal: this.signal });
    prev?.addEventListener('click', () => this.goTo(this.#index - 1), { signal: this.signal });
    next?.addEventListener('click', () => this.goTo(this.#index + 1), { signal: this.signal });
    for (const [i, button] of [...(this.refs.thumbs || []), ...(this.refs.dots || [])].entries()) {
      const count = this.slides.length;
      button.addEventListener('click', () => this.goTo(i % count), { signal: this.signal });
    }
    track.addEventListener('keydown', (event) => this.#onKey(event), { signal: this.signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.signal });

    const root = this.closest('[data-pg-product]') || document;
    root.addEventListener(
      'pg:variant:change',
      (event) => {
        const media = /** @type {CustomEvent} */ (event).detail?.variant?.media;
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(media));
        if (index >= 0) this.goTo(index);
      },
      { signal: this.signal },
    );

    // Start on the selected variant's image (e.g. /products/x?variant=…) without animating.
    const start = this.slides.findIndex((slide) => slide.dataset.mediaId === this.dataset.start);
    if (start > 0) requestAnimationFrame(() => this.goTo(start, false));
    this.#update();
  }

  /** @param {number} index @param {boolean} [smooth] */
  goTo(index, smooth = true) {
    const { track } = this.refs;
    const slide = this.slides[Math.max(0, Math.min(this.slides.length - 1, index))];
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { track, prev, next, counter } = this.refs;
    const width = track.clientWidth || 1;
    const index = Math.round(track.scrollLeft / width);
    if (index !== this.#index) {
      // Pause a video that scrolled out of view.
      this.slides[this.#index]?.querySelector('video')?.pause();
    }
    this.#index = index;
    for (const list of [this.refs.thumbs || [], this.refs.dots || []]) {
      list.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
    }
    const thumb = this.refs.thumbs?.[index];
    if (thumb && thumb.parentElement?.parentElement) {
      const strip = thumb.parentElement.parentElement;
      const left = thumb.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'auto' });
      }
    }
    if (counter) counter.textContent = String(index + 1);
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= this.slides.length - 1;
  }

  /** @param {KeyboardEvent} event */
  #onKey(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
    }
  }

  /** @param {MouseEvent} event */
  async #onClick(event) {
    const target = /** @type {Element} */ (event.target);

    const load = target.closest('[data-pg-deferred-load]');
    if (load) {
      const holder = load.closest('[data-pg-deferred]');
      const template = holder?.querySelector('template');
      if (holder && template) {
        holder.append(template.content.cloneNode(true));
        load.remove();
        const model = holder.querySelector('model-viewer');
        if (model) this.#loadModelViewer(model);
      }
      return;
    }

    const zoom = target.closest('[data-pg-zoom]');
    if (zoom) {
      const { openZoom } = await import(this.dataset.zoomModule || '');
      openZoom(this.dataset.zoomDialog, Number(zoom.getAttribute('data-pg-zoom')) || 0, zoom);
    }
  }

  /** 3D models use Shopify's model viewer UI, loaded on demand. */
  #loadModelViewer(model) {
    const shopify = /** @type {any} */ (window).Shopify;
    shopify?.loadFeatures?.([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error) return;
          const ui = new shopify.ModelViewerUI(model);
          ui.play?.();
        },
      },
    ]);
  }
}

define('pg-product-gallery', PgProductGallery);
